import React from "react";

const ClassicBusinessQuotation = ({ data }) => {
    const {
        to,
        clientAddress,
        clientPhone,
        clientEmail,
        title,
        date,
        expiryDate,
        items,
        subTotal,
        taxRate,
        taxAmount,
        discount,
        totalAmount,
        notes,
        terms,
        companyDetails
    } = data;

    return (
        <div className="w-full bg-white p-16 font-serif text-slate-900 border-[1px] border-slate-200 min-h-[1100px] flex flex-col justify-between shadow-2xl shadow-slate-100">
            <div>
                {/* Formal Header */}
                <div className="flex justify-between items-start border-b-[6px] border-slate-900 pb-12 mb-16 underline underline-offset-8 decoration-slate-900 decoration-2 italic">
                    <div className="space-y-6">
                        {companyDetails?.logo && (
                            <img src={companyDetails.logo} alt="Logo" className="h-16 w-auto grayscale contrast-125 italic" />
                        )}
                        <div className="space-y-2">
                            <h2 className="text-3xl font-black uppercase tracking-tight text-slate-900 italic underline underline-offset-4 decoration-slate-900 decoration-4">{companyDetails?.name}</h2>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-loose max-w-sm italic">{companyDetails?.address}</p>
                        </div>
                    </div>

                    <div className="text-right space-y-6">
                        <h1 className="text-6xl font-black uppercase tracking-widest text-slate-100 italic leading-none mb-4 -mr-10">QUOTE</h1>
                        <div className="space-y-2 border-r-4 border-slate-900 pr-6 italic">
                            <div className="text-lg font-black text-slate-900 uppercase italic">Ref: {title}</div>
                            <div className="text-xs font-bold text-slate-400 uppercase tracking-widest italic font-bold">Estimated Cost Proposal</div>
                        </div>
                    </div>
                </div>

                {/* Information Grid */}
                <div className="grid grid-cols-2 gap-20 mb-20 italic">
                    <div className="space-y-6">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-300 border-b-2 border-slate-900 pb-2 italic underline underline-offset-4 decoration-slate-100 decoration-2">Quotation Recipient</h4>
                        <div className="space-y-2">
                            <div className="text-2xl font-black text-slate-900 italic tracking-tight underline underline-offset-4 decoration-slate-900 decoration-4">{to}</div>
                            <div className="text-sm font-medium text-slate-500 italic max-w-xs">{clientAddress}</div>
                            <div className="flex flex-col pt-4 space-y-2 text-xs font-bold text-slate-400 tracking-widest italic underline underline-offset-4 decoration-slate-50 decoration-1 uppercase">
                                {clientPhone && <span>Contact: {clientPhone}</span>}
                                {clientEmail && <span>Email: {clientEmail}</span>}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6 bg-slate-50 p-8 rounded-2xl border-4 border-slate-900 italic shadow-xl shadow-slate-200">
                        <div className="grid grid-cols-2 gap-4 text-[10px] font-black uppercase tracking-widest text-slate-900 italic leading-loose">
                            <div>Proposal Date</div>
                            <div className="border-b border-slate-300 font-bold">{date}</div>
                            <div>Validity End</div>
                            <div className="text-slate-900 font-black">{expiryDate}</div>
                            <div>Type</div>
                            <div className="border-b border-slate-300 font-bold">Formal Estimate</div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="mb-20 italic">
                    <table className="w-full text-left italic">
                        <thead>
                            <tr className="bg-slate-900 text-white font-black uppercase text-[10px] tracking-widest italic font-bold">
                                <th className="p-6 italic underline underline-offset-4 decoration-slate-400 decoration-2">Service Execution Details</th>
                                <th className="p-6 text-center italic border-l border-white/10">Qty</th>
                                <th className="p-6 text-right italic border-l border-white/10">Estimated Rate</th>
                                <th className="p-6 text-right italic border-l border-white/10">Row Totals</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y-4 divide-slate-100 italic text-xs uppercase tracking-widest font-black leading-loose">
                            {(items || []).map((item, idx) => (
                                <tr key={idx} className="hover:bg-slate-100 transition-colors italic whitespace-nowrap overflow-hidden text-ellipsis">
                                    <td className="p-8 italic">
                                        <div className="text-lg font-black text-slate-900 italic tracking-tight uppercase leading-none underline underline-offset-4 decoration-slate-900 decoration-4 italic font-black">{item.item}</div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-4 font-bold border-l-4 border-slate-900 pl-4 italic">Deliverable Component Ref #0{idx + 1}</div>
                                    </td>
                                    <td className="p-8 text-center text-slate-400 italic font-black">{item.qty}</td>
                                    <td className="p-8 text-right text-slate-900 italic font-black">Nu. {parseFloat(item.unitPrice).toLocaleString()}</td>
                                    <td className="p-8 text-right text-lg font-black text-slate-900 italic bg-slate-50 font-black italic">Nu. {parseFloat(item.amount).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Bottom Section */}
            <div>
                <div className="flex justify-between items-start mb-16 border-t-8 border-slate-900 pt-16 italic">
                    <div className="max-w-[400px] space-y-6">
                        <div className="space-y-4 font-bold italic">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-900 italic underline underline-offset-4 decoration-slate-900 decoration-1 whitespace-nowrap overflow-hidden text-ellipsis">Contractual Terms</h4>
                            <p className="text-[10px] text-slate-500 tracking-widest leading-loose italic uppercase font-black underline underline-offset-4 decoration-slate-100 decoration-2">
                                {notes || "Our proposal is built upon precision and transparency. We look forward to a successful cooperation."}
                                <br /><br />
                                {terms || "Prices are subject to change if project scope deviates. Validity is strictly enforced."}
                            </p>
                        </div>
                    </div>

                    <div className="w-80 space-y-4 font-black italic uppercase tracking-widest text-[10px] leading-loose">
                        <div className="flex justify-between border-b-2 border-slate-100 pb-2">
                            <span>Project Subtotal</span>
                            <span className="text-slate-900 font-black">Nu. {parseFloat(subTotal).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between border-b-2 border-slate-100 pb-2">
                            <span>Taxation Estimate ({taxRate}%)</span>
                            <span className="text-slate-900 font-black">Nu. {parseFloat(taxAmount).toLocaleString()}</span>
                        </div>
                        {discount > 0 && (
                            <div className="flex justify-between border-b-2 border-slate-50 pb-2 text-slate-400 italic">
                                <span>Enterprise Volume Deduction</span>
                                <span>- Nu. {parseFloat(discount).toLocaleString()}</span>
                            </div>
                        )}
                        <div className="pt-10 flex justify-between items-end border-b-[12px] border-slate-900 pb-4 italic">
                            <span className="text-xs font-black uppercase tracking-[0.3em] text-slate-900 italic leading-none whitespace-nowrap overflow-hidden text-ellipsis underline underline-offset-4 decoration-slate-900 decoration-1 font-black underline-offset-8">Final Estimate</span>
                            <div className="text-5xl font-black text-slate-900 tracking-tighter italic leading-none font-black italic">Nu. {parseFloat(totalAmount).toLocaleString()}</div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-[0.4em] text-slate-300 italic font-bold">
                    <div>Official Multi-Currency Strategic Proposal</div>
                    <div className="italic text-slate-900 underline underline-offset-2 decoration-slate-100 decoration-1">Powered by NexrBook Technology</div>
                </div>
            </div>
        </div>
    );
};

export default ClassicBusinessQuotation;
